import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private currentLanguageSubject = new BehaviorSubject<string>('fr');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Set default language
    this.translate.setDefaultLang('fr');

    // Get language from localStorage or use default (only in browser)
    const savedLanguage = this.getStoredLanguage() || 'fr';
    this.setLanguage(savedLanguage);
  }

  /**
   * Safely get language from localStorage
   */
  private getStoredLanguage(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('language');
    }
    return null;
  }

  /**
   * Safely set language in localStorage
   */
  private setStoredLanguage(language: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('language', language);
    }
  }

  /**
   * Set the current language
   * @param language - The language code (fr, en, etc.)
   */
  setLanguage(language: string): void {
    this.translate.use(language);
    this.currentLanguageSubject.next(language);
    this.setStoredLanguage(language);
  }

  /**
   * Get the current language
   * @returns The current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Get available languages
   * @returns Array of available language codes
   */
  getAvailableLanguages(): string[] {
    return ['fr', 'en'];
  }

  /**
   * Get language name for display
   * @param languageCode - The language code
   * @returns The display name of the language
   */
  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      fr: 'Français',
      en: 'English',
    };
    return languageNames[languageCode] || languageCode;
  }

  /**
   * Toggle between French and English
   */
  toggleLanguage(): void {
    const currentLang = this.getCurrentLanguage();
    const newLang = currentLang === 'fr' ? 'en' : 'fr';
    this.setLanguage(newLang);
  }
}
